import { Grid } from "@mui/material";
import dayjs from "dayjs";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Button from "../../../framework/Button";
import Checkbox from "../../../framework/Checkbox";
import ChipInputSelect from "../../../framework/ChipInputSelect";
import DatePicker from "../../../framework/date/DatePicker";
import GlobalContext from "../../../framework/GlobalContext";
import SelectInput from "../../../framework/SelectInput";
import Shortcut from "../../../framework/Shortcut";
import TextInput from "../../../framework/TextInput";
import { displayToast } from "../../../framework/toast/toastUtils";
import { fetchBranches } from "../../../services/branch-service";
import { getCategory } from "../../../services/category-services";
import { getCustomerList } from "../../../services/customer-services";
import { addLoyaltyProgram } from "../../../services/discount-program";
import { listEntity } from "../../../services/entity-services";
import { executeScroll } from "../../../utils/utils";
import AddDiscounts from "./AddDiscounts";
import classes from "./index.module.css";
import { getISODateTime } from "../../../utils/utils";
import { listBranchGroup } from "../../../services/branch-group-services";
const AddDiscountProgram = (props) => {
  const {
    onEscape = () => {},
    refreshData = () => {},
    currentRowData,
    isEdit = false,
    programType = "",
  } = props;
  const [programName, setProgramName] = useState("");
  const [customer, setCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [branch, setBranch] = useState([]);
  const [branchesOptions, setBranchOptions] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [discounts, setDiscounts] = useState([
    {
      referenceId: null,
      discPer: null,
    },
  ]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [errorStates, setErrorStates] = useState({});
  const programNameRef = useState(null);
  const startDateRef = useRef();
  const endDateRef = useRef();
  const customerCategoryRef = useRef();
  const branchRef = useRef();
  const addDiscountsButtonRef = useRef();
  const saveDiscountsButtonRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);
  const showDeleteIcon = useMemo(() => {
    if (discounts?.length === 1) {
      return false;
    }
    return true;
  }, [discounts?.length]);
  const fetchCustomerData = useCallback(async () => {
    if (programType === "LOYALTY") {
      await getCustomerList().then(async (response) => {
        let allCustomers = response?.result?.rows;
        let loyaltiCustomers = await allCustomers.filter(
          (x) =>
            x?.patientCategoryData?.name === "LOYALITY" ||
            x?.patientCategoryData?.name === "LOYALTY"
        );
        setCustomers(loyaltiCustomers);
      });
    }
    if (programType === "CORPORATE") {
      let entities = await listEntity();
      const corporateCustomers = entities?.result?.rows?.filter(
        (x) => x?.entityCategoryData?.name === "CORPORATE"
      );
      setCustomers(corporateCustomers);
    }
  }, [programType]);
  const fetchAllBranches = useCallback(async () => {
    try {
      const _branch = await fetchBranches();
      const _branchGroup = await listBranchGroup();
      const mergedBranches = [
        ...(_branchGroup?.result?.rows || []),
        ...(_branch.result?.rows || []),
      ];

      setBranchOptions(mergedBranches || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  }, []);
  const addDiscountsRow = useCallback(() => {
    if (discounts.length === 1) {
      if (discounts[0].referenceId === null || discounts[0].discPer === null) {
        displayToast({
          type: "error",
          title: "Please enter at least one discount",
        });
        return;
      }
    }
    setDiscounts((prev) => {
      return [
        ...prev,
        {
          referenceId: null,
          discPer: 0,
        },
      ];
    });
  }, [discounts]);
  const deleteDiscountsHandler = useCallback(
    (index) => {
      if (discounts.length <= 1) return;
      const _discounts = [...discounts];
      const filteredDiscounts = _discounts.filter((_, i) => i !== index);
      setDiscounts(filteredDiscounts);
      setTimeout(() => {
        let newIndex = index;
        if (newIndex === filteredDiscounts.length) {
          newIndex -= 1;
        }
        document
          .getElementById(`row_item_0_${newIndex}`)
          ?.focus({ preventScroll: true });
      }, 100);
    },
    [discounts]
  );
  const validateDiscounts = useCallback(() => {
    const hasError = discounts.some((discount, index) => {
      if (discount.referenceId === null || discount.discPer === null) {
        displayToast({
          type: "error",
          title:
            discount.referenceId === null
              ? `Please select product category for row ${index + 1}`
              : `Please enter discount for row ${index + 1}`,
        });
        return true;
      }
      const isDuplicate = discounts.some(
        (item, idx) =>
          idx !== index && item.referenceId === discount.referenceId
      );
      if (isDuplicate) {
        displayToast({
          type: "error",
          title: `Duplicate entry for row ${index + 1}`,
        });
        return true;
      }
      return false;
    });
    return !hasError;
  }, [discounts]);
  const validateForm = useCallback(async () => {
    if (!programName) {
      displayToast({
        type: "error",
        title: "Please enter discount program name",
      });
      setErrorStates((prev) => {
        return { ...prev, programName: true };
      });
      programNameRef.current.focus({ preventScroll: true });
      setCurrentComponent("add_discount_program_program_name");
      return false;
    }
    if (!startDate) {
      displayToast({
        type: "error",
        title: "Please select start date",
      });
      setErrorStates((prev) => {
        return { ...prev, startDate: true };
      });
      startDateRef.current.focus({ preventScroll: true });
      setCurrentComponent("add_discount_program_start_date");
      return false;
    }
    if (!endDate) {
      displayToast({
        type: "error",
        title: "Please select end date",
      });
      setErrorStates((prev) => {
        return { ...prev, endDate: true };
      });
      endDateRef.current.focus({ preventScroll: true });
      setCurrentComponent("add_discount_program_end_date");
      return false;
    }
    if (endDate < startDate) {
      displayToast({
        type: "error",
        title: "End date cannot be earlier than start date",
      });
      setErrorStates((prev) => {
        return { ...prev, endDate: true };
      });
      endDateRef.current.focus({ preventScroll: true });
      setCurrentComponent("add_discount_program_end_date");
      return false;
    }
    if (!customer) {
      displayToast({
        type: "error",
        title:
          programType === "LOYALTY"
            ? "Please select customer"
            : "Please select entity",
      });
      setErrorStates((prev) => {
        return { ...prev, customer: true };
      });
      customerCategoryRef.current.focus({ preventScroll: true });
      setCurrentComponent("add_discount_program_customer");
      return false;
    }
    if (branch.length === 0) {
      displayToast({
        type: "error",
        title: "Please select branch",
      });
      setErrorStates((prev) => {
        return { ...prev, branch: true };
      });
      branchRef.current.focus({ preventScroll: true });
      setCurrentComponent("add_discount_program_branch");
      return false;
    }
    if (discounts.length === 1) {
      if (discounts[0].referenceId === null || discounts[0].discPer === null) {
        displayToast({
          type: "error",
          title: "Please enter at least one discount",
        });
        return;
      }
    } else if (discounts.length > 1) {
      if (!validateDiscounts()) {
        return;
      }
    }
    return true;
  }, [
    branch.length,
    customer,
    discounts,
    endDate,
    programType,
    programName,
    programNameRef,
    setCurrentComponent,
    startDate,
    validateDiscounts,
  ]);
  const saveDiscountProgramHandler = useCallback(async () => {
    if (!(await validateForm())) {
      return;
    }
    const discountDetails = discounts.map((discount) => {
      return {
        ...discount,
        referenceType: "category",
        deleted: isEdit ? discount?.deleted : false,
      };
    });
    const selectedBranchIds = branch?.reduce((acc, branch) => {
      if (branch?.branchCode) {
        acc.push(...branch.branchCode);
      } else if (branch?.code) {
        acc.push(branch.code);
      }
      return acc;
    }, []);
    const uniqueBranchIds = [...new Set(selectedBranchIds)];
    const payload = {
      programName: programName,
      startDate: startDate,
      endDate: endDate,
      defaultProgram: false,
      loyaltyProgramDetails: discountDetails,
      branchCode: uniqueBranchIds,
      loyaltyProgramCode: isEdit
        ? currentRowData?.discountProgramCode || null
        : null,
      remarks: remarks,
      referenceId:
        programType === "LOYALTY" ? customer?.patientId : customer?.entityId,
      referenceType: programType === "LOYALTY" ? "patient" : "entity",
    };
    await addLoyaltyProgram(payload).then((response) => {
      if (response?.status === "success") {
        displayToast({
          type: "success",
          title: isEdit
            ? "Discount program updated successfully."
            : "Discount program added successfully.",
        });
        onEscape();
        refreshData();
      }
    });
  }, [
    validateForm,
    discounts,
    branch,
    programName,
    startDate,
    endDate,
    isEdit,
    currentRowData?.discountProgramCode,
    remarks,
    programType,
    customer?.patientId,
    customer?.entityId,
    onEscape,
    refreshData,
  ]);
  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      const ctrlKey = event.ctrlKey;
      if (key === "escape") {
        onEscape();
        return;
      }
      if (key === "+") {
        addDiscountsButtonRef?.current?.click();
        return;
      }
      if (ctrlKey && key === "s") {
        saveDiscountsButtonRef?.current?.click();
        return;
      }
    },
    [onEscape]
  );

  const findCategoryInHierarchy = useCallback(
    (categories, categoryId, parent = null) => {
      for (const category of categories) {
        if (category.categoryId === categoryId || category.id === categoryId) {
          return {
            value: category,
            parent: parent ? { ...parent } : null,
          };
        }
        if (category.childs && category.childs.length > 0) {
          const found = findCategoryInHierarchy(category.childs, categoryId, {
            value: category,
            parent: parent ? { ...parent } : null,
          });
          if (found) {
            return found;
          }
        }
      }
      return null;
    },
    []
  );

  const initializeEdit = useCallback(async () => {
    if (!isEdit || !currentRowData) return;
    setProgramName(currentRowData.programName || "");
    const getStartDate = dayjs(currentRowData.startDate);
    if (getStartDate.isValid()) {
      setStartDate(getStartDate);
    }
    const getEndDate = dayjs(currentRowData.endDate);
    if (getEndDate.isValid()) {
      setEndDate(getEndDate);
    }
    const customer = customers.find((x) => {
      return x.patientId === currentRowData?.referenceId;
    });
    setCustomer(customer || null);
    const branchCode = currentRowData.branchCode;
    const filteredBranches = branchesOptions.filter((b) =>
      branchCode.includes(b.code)
    );
    setBranch(filteredBranches || []);
    setRemarks(currentRowData?.remarks || "");
    const categories = await getCategory();
    const categoryDetails = await Promise.all(
      currentRowData?.loyaltyProgramDetails?.map(async (details) => {
        const categoryData = await findCategoryInHierarchy(
          categories,
          details?.referenceId
        );
        return {
          ...details,
          categoryData: categoryData,
        };
      })
    );
    setDiscounts(categoryDetails);
  }, [
    isEdit,
    currentRowData,
    customers,
    branchesOptions,
    findCategoryInHierarchy,
  ]);

  useEffect(() => {
    document.addEventListener("keyup", keyupListener, false);
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [keyupListener]);
  useEffect(() => {
    fetchCustomerData();
  }, [fetchCustomerData]);
  useEffect(() => {
    fetchAllBranches();
  }, [fetchAllBranches]);
  useEffect(() => {
    programNameRef.current.focus({ preventScroll: true });
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    initializeEdit();
  }, [initializeEdit]);

  return (
    <div id="add_discount_program" className={classes["add-discount-program"]}>
      <div className={classes["add-discount-program-header"]}>
        <div className={classes["add-discount-program-header-label"]}>
          {programType === "LOYALTY"
            ? "Add Loyalty Discount Program"
            : "Add Corporate Discount Program"}
        </div>
        <div className={classes["add-discount-program-header-fields"]}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextInput
                id="add_discount_program_program_name"
                label="Discount Program Name"
                ref={programNameRef}
                value={programName}
                onChange={(e) => {
                  setProgramName(e.target.value);
                  setErrorStates((prev) => {
                    return { ...prev, programName: false };
                  });
                }}
                required={true}
                error={errorStates.programName}
                onFocus={() =>
                  setCurrentComponent("add_discount_program_program_name")
                }
                onBlur={
                  !programName
                    ? () => {
                        setErrorStates((prev) => {
                          return { ...prev, programName: true };
                        });
                        displayToast({
                          type: "error",
                          title: "Please enter discount program name",
                        });
                      }
                    : () => {}
                }
                inputProps={{
                  "data-markers": JSON.stringify({
                    down: "add_discount_program_branch",
                    left: "add_discount_program_program_name",
                    right: "add_discount_program_start_date",
                    up: "add_discount_program_program_name",
                  }),
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <DatePicker
                id="add_discount_program_start_date"
                label="Start Date"
                inputRef={startDateRef}
                onChange={(value) => {
                  const formattedValue = getISODateTime(
                    dayjs(value).format("DD/MM/YYYY")
                  );
                  if (value && dayjs(value).isBefore(dayjs(), "day")) {
                    displayToast({
                      type: "error",
                      title: "Start date cannot be in the past",
                    });
                    setStartDate(dayjs());
                  } else {
                    setStartDate(formattedValue);
                    setErrorStates((prev) => {
                      return { ...prev, startDate: false };
                    });
                  }
                }}
                onFocus={() =>
                  setCurrentComponent("add_discount_program_start_date")
                }
                value={startDate ? dayjs(startDate) : null}
                onBlur={
                  !startDate
                    ? () => {
                        setErrorStates((prev) => {
                          return { ...prev, startDate: true };
                        });
                        displayToast({
                          type: "error",
                          title: "Please enter start date",
                        });
                      }
                    : () => {}
                }
                inputProps={{
                  "data-markers": JSON.stringify({
                    up: "add_discount_program_start_date",
                    down: "add_discount_program_start_date",
                    left: "add_discount_program_program_name",
                    right: "add_discount_program_end_date",
                  }),
                }}
                disablePast={true}
                required={true}
                error={errorStates.startDate}
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={3}>
              <DatePicker
                id="add_discount_program_end_date"
                label="End Date"
                inputRef={endDateRef}
                onChange={(value) => {
                  const formattedValue = getISODateTime(
                    dayjs(value).format("DD/MM/YYYY")
                  );
                  if (value && dayjs(value).isBefore(dayjs(), "day")) {
                    displayToast({
                      type: "error",
                      title: "End date cannot be in the past",
                    });
                    setEndDate(startDate || dayjs());
                  } else if (dayjs(value).isBefore(startDate, "day")) {
                    setEndDate(startDate || dayjs());
                  } else {
                    setEndDate(formattedValue);
                    setErrorStates((prev) => {
                      return { ...prev, endDate: false };
                    });
                  }
                }}
                onFocus={() => {
                  executeScroll(endDateRef);
                  setCurrentComponent("add_discount_program_end_date");
                }}
                value={endDate ? dayjs(endDate) : null}
                onBlur={
                  !endDate
                    ? () => {
                        setErrorStates((prev) => {
                          return { ...prev, endDate: true };
                        });
                        displayToast({
                          type: "error",
                          title: "Please enter end date",
                        });
                      }
                    : () => {
                        if (dayjs(endDate).isSame(startDate, "day")) {
                          displayToast({
                            type: "warn",
                            title: "Start and end dates are same",
                          });
                        }
                      }
                }
                inputProps={{
                  "data-markers": JSON.stringify({
                    up: "add_discount_program_end_date",
                    down: "add_discount_program_end_date",
                    left: "add_discount_program_start_date",
                    right: "add_discount_program_customer",
                  }),
                }}
                disablePast={true}
                required={true}
                error={errorStates.endDate}
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={3}>
              <SelectInput
                id="add_discount_program_customer"
                label={
                  programType === "LOYALTY" ? "Loyalty Customers" : "Entities"
                }
                ref={customerCategoryRef}
                options={customers}
                getOptionLabel={(option) => {
                  return programType === "LOYALTY"
                    ? option.patientName || option
                    : option.entityName || option;
                }}
                value={customer}
                isOptionEqualToValue={(option, value) =>
                  programType === "LOYALTY"
                    ? option?.patientId === value?.patientId
                    : option?.entityId === value?.entityId
                }
                onFocus={() =>
                  setCurrentComponent("add_discount_program_customer")
                }
                onChange={(e, option) => {
                  setErrorStates((prev) => {
                    return { ...prev, customer: false };
                  });
                  setCustomer(option);
                }}
                onBlur={
                  !customer
                    ? () => {
                        setErrorStates((prev) => {
                          return { ...prev, customer: true };
                        });
                        displayToast({
                          type: "error",
                          title:
                            programType === "LOYALTY"
                              ? "Please select customer"
                              : "Please select entity",
                        });
                      }
                    : () => {}
                }
                name="customer"
                markers={JSON.stringify({
                  up: "add_discount_program_start_date",
                  down: "add_discount_program_branch",
                  left: "add_discount_program_end_date",
                  right: "add_discount_program_branch",
                })}
                required={true}
                error={errorStates.customer}
              />
            </Grid>
            <Grid item xs={9}>
              <ChipInputSelect
                id="add_discount_program_branch"
                value={branch || null}
                options={branchesOptions}
                name="branch"
                ref={branchRef}
                getOptionLabel={(option) =>
                  `${option?.name || ""}- ${option?.code ? "BR" : "BG"}`
                }
                onFocus={() => {
                  setCurrentComponent("add_discount_program_branch");
                }}
                onChange={(event, option) => {
                  setBranch(option);
                  setErrorStates((prev) => {
                    return { ...prev, branch: false };
                  });
                }}
                onBlur={
                  branch?.length === 0
                    ? () => {
                        setErrorStates((prev) => {
                          return { ...prev, branch: true };
                        });
                        displayToast({
                          type: "error",
                          title: "Please enter branch",
                        });
                      }
                    : () => {}
                }
                error={errorStates.branch}
                required={true}
                openOnFocus={true}
                label="Branches"
                multiple={true}
                blurOnSelect={false}
                disableCloseOnSelect={true}
                groupBy={(option) =>
                  option?.code ? "Branches" : "Branch Groups"
                }
                renderGroup={(params) => {
                  return (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        padding: "5px",
                        gap: "5px",
                      }}
                      key={params.key}
                    >
                      <div
                        style={{
                          height: "30px",
                          width: "100%",
                          background: "#e0e0e0",
                          display: "flex",
                          alignItems: "center",
                          padding: "5px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        {params.group}
                      </div>
                      <div>{params.children}</div>
                    </div>
                  );
                }}
                renderOption={(props, option, { selected }) => {
                  return (
                    <div
                      {...props}
                      style={{
                        display: "flex",
                        height: "40px",
                        width: "100%",
                        padding: "1px 5px",
                        gap: "5px",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItem: "center",
                        }}
                      >
                        <Checkbox checked={selected} />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItem: "center",
                          gap: "5px",
                        }}
                      >
                        <div
                          style={{
                            height: "15px",
                            width: "100%",
                            alignItems: "center",
                          }}
                        >
                          {option?.name} (
                          {option?.code || option?.branchGroupId})
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {option.address}
                        </div>
                      </div>
                    </div>
                  );
                }}
                markers={JSON.stringify({
                  up: "add_discount_program_start_date",
                  down: "add_discount_program_min",
                  left: "add_discount_program_customer",
                  right: "add_discount_program_remarks",
                })}
              />
            </Grid>
            <Grid item xs={3}>
              <TextInput
                id="add_discount_program_remarks"
                label="Remarks"
                value={remarks}
                onChange={(e) => {
                  setRemarks(e.target.value);
                }}
                onFocus={() =>
                  setCurrentComponent("add_discount_program_remarks")
                }
                inputProps={{
                  "data-markers": JSON.stringify({
                    up: "add_discount_program_customer",
                    down: "row_item_0_0",
                    left: "add_discount_program_branch",
                    right: "row_item_0_0",
                  }),
                }}
              />
            </Grid>
          </Grid>
        </div>
      </div>
      <div className={classes["add-discounts-subcontainer"]}>
        <div style={{ height: "auto", maxHeight: "85%", width: "100%" }}>
          <div className={classes["add-discounts-title-container"]}>
            <div className={classes["add-discounts-title"]}>Add Discounts</div>
            <Button
              label="ADD"
              color="black"
              ref={addDiscountsButtonRef}
              onClick={addDiscountsRow}
              endIcon={<Shortcut keys={["+"]} />}
            />
          </div>
          <div className={classes["add-discounts-details-container"]}>
            <div className={classes["add-discounts-details"]}>
              {discounts?.map((discount, index) => {
                return (
                  <AddDiscounts
                    key={`${index}_${discount.name}`}
                    index={index}
                    discountRowData={discount}
                    showDeleteIcon={showDeleteIcon}
                    onDeleteDiscount={() => deleteDiscountsHandler(index)}
                    onChange={(key, data) => {
                      setDiscounts((prev) => {
                        prev[index][key] = data;
                        return [...prev];
                      });
                    }}
                    isEdit={isEdit}
                    discounts={discounts}
                  />
                );
              })}
            </div>
          </div>
        </div>
        <div className={classes["add-discounts-cta-container"]}>
          <Button
            label="Save"
            ref={saveDiscountsButtonRef}
            onClick={saveDiscountProgramHandler}
            color="violet"
            endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          />
        </div>
      </div>
    </div>
  );
};

export default AddDiscountProgram;
